import type { ScaleModel } from "../../../lib/stageViewModels";
import { Empty } from "./McqBars";
import { useGrowIn } from "./useGrowIn";

/** Stimmung: one stacked bar, kritisch | mitte | zustimmend, with the
 *  agreeing share as the hero number. */
export function ScaleSentiment({ model }: { model: ScaleModel }) {
  const { grown, reduced } = useGrowIn();
  const { low, mid, high, lowLabel, midLabel, highLabel } = model.bands;
  const total = low + mid + high;
  if (total === 0) return <Empty />;
  const pct = (n: number) => Math.round((n / total) * 100);
  const bands = [
    {
      key: "low",
      label: lowLabel,
      n: low,
      cls: "bg-rc-paper/15 text-rc-paper/80",
    },
    {
      key: "mid",
      label: midLabel,
      n: mid,
      cls: "bg-rc-paper/35 text-rc-paper",
    },
    {
      key: "high",
      label: highLabel,
      n: high,
      cls: "bg-rc-berry text-rc-paper shadow-berry",
    },
  ];
  return (
    <div className="flex flex-col h-full justify-center gap-10 text-rc-paper">
      <header className="flex flex-col items-center text-center">
        <span
          className="font-bold leading-none tabular-nums tracking-[-0.04em] text-rc-lime"
          style={{ fontSize: "clamp(4rem, 9vw, 8.5rem)" }}
        >
          {pct(high)}%
        </span>
        <span className="mt-2 text-[11px] font-bold uppercase tracking-[0.28em] text-rc-paper/55">
          {highLabel} · {total} {total === 1 ? "Antwort" : "Antworten"}
        </span>
      </header>
      <div
        className={`flex items-stretch gap-1 h-16 rounded-rc-lg overflow-hidden ${reduced ? "" : "transition-opacity duration-700"}`}
        style={{ opacity: grown ? 1 : 0 }}
      >
        {bands.filter((b) => b.n > 0).map((b) => (
          <div
            key={b.key}
            style={{
              flex: b.n,
              transition: reduced ? "none" : "flex 700ms ease-out",
            }}
            className={`min-w-[3rem] flex items-center justify-center gap-2 text-[15px] font-semibold tabular-nums ${b.cls}`}
            aria-label={`${b.label}: ${b.n} (${pct(b.n)} Prozent)`}
          >
            <span>{pct(b.n)}%</span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 text-[11px] font-bold uppercase tracking-[0.22em] text-rc-paper/55">
        <span>
          {lowLabel}{" "}
          <span className="text-rc-paper/30 font-medium tabular-nums">{low}</span>
        </span>
        <span className="text-center">
          {midLabel}{" "}
          <span className="text-rc-paper/30 font-medium tabular-nums">{mid}</span>
        </span>
        <span className="text-right">
          {highLabel}{" "}
          <span className="text-rc-paper/30 font-medium tabular-nums">
            {high}
          </span>
        </span>
      </div>
    </div>
  );
}
